import { faCircleTrash, faFloppyDisk, faMoneyFromBracket } from '@fortawesome/pro-light-svg-icons'
import { Fragment, useCallback, useMemo } from 'react'
import { ButtonPrimary, ButtonRed } from '../../../../../../../../../../baseComponents/Button'
import TwinIcon from '../../../../../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../../../../../baseComponents/TwinTrans'
import { SellTicketButtonsProps, SetTPVCompleteDataTy, TPVOrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/types'
import { twinFetchPostJSON } from '../../../../../../../../../../utils/globals/data'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import ModalDiscardSellTicket from '../ModalDiscardSellTicket'
import ModalSkipOperateStockSellTicket from '../ModalSkipOperateStockSellTicket'
import { parsedTicketTableData } from '../../../../../functions'
import { sellTicketStatusInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import { changeErrorMessage, changeMultiUser, deleteAntiUnsaveElement } from '../../../../../../../../../../utils/reducers/reduxDispatch'
import ModalPaymentMethodTPV from '../../../ModalPaymentMethodTPV'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import ModalPaymentMethodInfoTPV from '../../../ModalPaymentMethodTPV/ModalPaymentMethodInfoTPV'
import { getConfigParam } from '../../../../../../../../../../utils/reducers/getters'
import { appStore } from '../../../../../../../../../../utils/reducers'

interface SellTicketCreateStepButtonsProps extends SellTicketButtonsProps { 
}

const SellTicketCreateStepButtons: React.FC<SellTicketCreateStepButtonsProps> = ({ ...rest }) => {
    const noProducts = rest.completeData?.originalTableData?.length ? false : true
    const disabled = rest.completeData?.header?.id && !rest.completeData?.header?.notModificable ? false : true

    const mustChangeUser = useMemo(() => {
        const param = getConfigParam('askForEmployeeEveryTicket') || 'on'
        const subType = appStore.getState().user?.subType 
        return (param === 'on' && subType && subType === 'temporal') || false
    }, [])
    return (
        <div className='flex flex-row bg-white product_ticket_buttons overflow-hidden'>
            <DiscardSellTicketButton {...{ disabled, mustChangeUser, ...rest }} />
            <SaveSellTicketButton {...{ disabled, mustChangeUser, noProducts, ...rest }} />
            <ProcessSellTicketButton {...{ disabled, mustChangeUser, noProducts, ...rest }} />
        </div>
    )
}

interface SaveSellTicketButtonProps extends SaveSellTicketButtonLogicProps {
    disabled?: boolean
    noProducts: boolean
}

const SaveSellTicketButton: React.FC<SaveSellTicketButtonProps> = ({ disabled, noProducts, ...rest}) => {
    const { loading, handleSaveSellTicket } = useSaveSellTicketButtonLogic({ ...rest})
    return (
        <ButtonPrimary className={'flex-auto flex items-center justify-center ticket_button_no_border w-1/3 ' + (disabled || noProducts ? '' : 'save_ticket_button_parent')} onClick={handleSaveSellTicket} disabled={disabled || noProducts}>
            <TwinIcon icon={faFloppyDisk} className={'mr-8 text-white regular20 ' + (loading ? 'hidden' : '')} />
            <span>{loading ?
                <TwinTrans transKey='processingDotsv2' >Procesando...</TwinTrans>
                : <TwinTrans transKey='parkTicket'>Aparcar ticket</TwinTrans>}
            </span>
        </ButtonPrimary>
    )
}

interface SaveSellTicketButtonLogicProps extends SellTicketCreateStepButtonsProps {
    mustChangeUser: boolean
}

const useSaveSellTicketButtonLogic = ({ setCompleteData, inputTableURLs, completeData, mustChangeUser }: SaveSellTicketButtonLogicProps) => {
    const { endLoading, loading, startLoading } = useIsLoading()
    const handleSaveSellTicket = useCallback(async() => {
        startLoading?.()
        const result = await twinFetchPostJSON(inputTableURLs.updateHeaderData, { id: completeData?.header?.id, status: sellTicketStatusInversed['saved'] })
        if (result) {
            setCompleteData((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.lastProductIndex = null
                copyOld.header = {}
                copyOld.tableData = []
                copyOld.Customer = {}
                delete copyOld.selectedTableData
                parsedTicketTableData(copyOld)
                return copyOld
            })
            deleteAntiUnsaveElement('sellTicket')
        }
        if (mustChangeUser) {
            changeMultiUser(true)
        }
        endLoading?.()
    }, [startLoading, endLoading, setCompleteData, completeData?.header?.id, inputTableURLs.updateHeaderData, mustChangeUser])
    return { loading, handleSaveSellTicket }
}

interface ProcessSellTicketButtonProps extends ProcessSellTicketButtonLogicProps {
    disabled?: boolean
    noProducts: boolean
}

const ProcessSellTicketButton: React.FC<ProcessSellTicketButtonProps> = ({ disabled, noProducts, ...rest}) => {
    const { openModal, setOpenModal, myOnSubmit, handleProcessSellTicket, handleFinalizeSellTicket } = useProcessSellTicketButtonLogic({ ...rest })
    return (
        <Fragment>
            <ButtonPrimary className={'flex-auto flex items-center justify-center ticket_button_border_right w-1/3 ' + (disabled || noProducts ? '' : 'charge_ticket_button_parent')} onClick={handleProcessSellTicket} disabled={disabled || noProducts}>
                <TwinIcon icon={faMoneyFromBracket} className='mr-8 text-white regular20 ' />
                <TwinTrans transKey='charge'>Cobrar</TwinTrans>
            </ButtonPrimary>
            {openModal?.type === 'create' ? <ModalPaymentMethodTPV completeData={rest.completeData} setOpenModal={setOpenModal} url={'/api/app/order/tpv/sellTicket/chargeSellTicket'} onSubmit={myOnSubmit} isRefund={false} allRowData={openModal.allRowData} /> : null}
            {openModal?.type === 'edit' ? <ModalSkipOperateStockSellTicket setOpenModal={setOpenModal} allRowData={openModal?.allRowData} /> : null}
            {openModal?.type === 'delete' ? <ModalPaymentMethodInfoTPV setOpenModal={setOpenModal} allRowData={openModal?.allRowData} inputTableURLs={rest.inputTableURLs} handleCloseModal={handleFinalizeSellTicket} /> : null}
        </Fragment>
    )
}

interface ProcessSellTicketButtonLogicProps extends SellTicketCreateStepButtonsProps {
    mustChangeUser: boolean
}

const useProcessSellTicketButtonLogic = ({ setCompleteData, completeData, mustChangeUser }: ProcessSellTicketButtonLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const handleProcessSellTicket = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/checkAllSellTicketProductsStock', { id: completeData?.header?.id })
        if (result?.length) {
            const myExtraProps = { extraProps: { id: completeData?.header?.id, WorkingCenterId: completeData?.header?.WorkingCenterId} }
            setOpenModal({ type: 'edit', allRowData: { stockErrors: result, ...myExtraProps } })
        } else {
            setOpenModal({ type: 'create', allRowData: { id: completeData?.header?.id } })
        }
    }, [setOpenModal, completeData])

    const myOnSubmit = useCallback(async () => {
        await deleteAntiUnsaveElement('sellTicket')
        setCompleteData((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.lastProductIndex = null
            copyOld.header = {}
            copyOld.tableData = []
            copyOld.Customer = {}
            delete copyOld.selectedTableData
            parsedTicketTableData(copyOld)
            return copyOld
        })
    }, [setCompleteData])

    const handleFinalizeSellTicket = useCallback(async () => {
        setOpenModal(null)
        if (mustChangeUser){
            changeMultiUser(true)
        }
    }, [mustChangeUser, setOpenModal])

    return { openModal, setOpenModal, myOnSubmit, handleProcessSellTicket, handleFinalizeSellTicket }
}


interface DiscardSellTicketButtonProps extends DiscardSellTicketButtonLogicProps {
    disabled?: boolean
}

const DiscardSellTicketButton: React.FC<DiscardSellTicketButtonProps> = ({ disabled, ...rest }) => {
    const { loading, handleDiscardSellTicket, openModal, setOpenModal } = useDiscardSellTicketButtonLogic({ ...rest })
    return (
        <ButtonRed className={'flex-auto flex items-center justify-center ticket_button_border_left w-1/3 ' + (disabled ? '' : 'red_ticket_button_parent')}onClick={() => setOpenModal({ type: 'edit', allRowData: {} })} disabled={disabled}>
            <TwinIcon icon={faCircleTrash} className={'mr-8 text-white regular20 ' + (loading ? 'hidden' : '')} />
            <span>{loading ?
                <TwinTrans transKey='processingDotsv2' >Procesando...</TwinTrans>
                : <TwinTrans transKey='discardTicket'>Descartar ticket</TwinTrans>}
            </span>
            {openModal?.type === 'edit' ? <ModalDiscardSellTicket setOpened={() => setOpenModal(null)} handleOnAccept={handleDiscardSellTicket} onSubmit={() => {}}/> : null}
        </ButtonRed>
    )
}

interface DiscardSellTicketButtonLogicProps extends SellTicketCreateStepButtonsProps {
    setCompleteData: SetTPVCompleteDataTy
    completeData: TPVOrderData
    mustChangeUser: boolean
}

const useDiscardSellTicketButtonLogic = ({ setCompleteData, completeData, mustChangeUser }: DiscardSellTicketButtonLogicProps) => {
    const { t } = useTwinTranslation()
    const errorMsg = t('canNotDeleteSellTicket', 'No se ha podido borrar el ticket')
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { endLoading, loading, startLoading } = useIsLoading()
    const handleDiscardSellTicket = useCallback(async () => {
        startLoading?.()
        const result = await twinFetchPostJSON('/api/app/order/tpv/sellTicket/deleteSellTicketDraft', { id: completeData?.header?.id })
        if (result) {
            setCompleteData((old) => {
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.lastProductIndex = null
                copyOld.header = {}
                copyOld.tableData = []
                copyOld.Customer = {}
                delete copyOld.selectedTableData
                parsedTicketTableData(copyOld)
                return copyOld
            })
            await deleteAntiUnsaveElement('sellTicket')
            if (mustChangeUser) {
                changeMultiUser(true)
            }
        } else {
            changeErrorMessage(errorMsg)
        }
        endLoading?.()
    }, [startLoading, endLoading, setCompleteData, completeData?.header?.id, errorMsg, mustChangeUser])
    return { loading, handleDiscardSellTicket, openModal, setOpenModal }
}


export default SellTicketCreateStepButtons